import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import MicroSoftSquareIcon from '../../assets/bombiix-icons/MicroSoftSquareIcon';
import MicroSoftRoundIcon from '../../assets/bombiix-icons/MicroSoftRoundIcon';
import GoogleLogoIcon from '../../assets/bombiix-icons/GoogleLogoIcon';
import UserIcon from '../../assets/bombiix-icons/UserIcon';
import ActiveIcon from '../../assets/bombiix-icons/ActiveIcon';
import DisconnectIcon from '../../assets/bombiix-icons/DisconnectIcon';
import CloseIcon from '../../assets/bombiix-icons/CloseIcon';
import {
  listSsoDirectories,
  getSsoSeatUsage,
  startSsoConnect,
  setSsoDirectoryJit,
  disconnectSsoDirectory,
  logOut,
} from '../../services/auth.service';
import { localStorageService } from '../../services/localStorage.service';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import './SsoSettings.css';

const DISCONNECT_CONFIRM_WORD = 'confirm';

const SSO_PROVIDERS = [
  { key: 'microsoft', labelKey: 'SSO_SETTINGS.PROVIDER_MICROSOFT', SquareIcon: MicroSoftSquareIcon, RoundIcon: MicroSoftRoundIcon },
  { key: 'google', labelKey: 'SSO_SETTINGS.PROVIDER_GOOGLE', SquareIcon: GoogleLogoIcon, RoundIcon: GoogleLogoIcon },
];

/**
 * Admin-only Settings page for managing the company's connected SSO directories. The left rail
 * lists the identity providers (Microsoft, Google); selecting one shows that provider's
 * connected directories as cards with status and a disconnect action, plus company-wide seat
 * usage.
 * @returns the SSO settings view.
 */
function SsoSettings() {
  const { t } = useTranslation('sso');
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [directoriesByProvider, setDirectoriesByProvider] = useState({ microsoft: [], google: [] });
  const [selectedProvider, setSelectedProvider] = useState(() => {
    const returned = new URLSearchParams(location.search).get('provider');
    return SSO_PROVIDERS.some((provider) => provider.key === returned) ? returned : SSO_PROVIDERS[0].key;
  });
  const [seatUsage, setSeatUsage] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const [storedUser, storedUserUnreadable] = (() => {
    try {
      return [JSON.parse(localStorage.getItem('loggedInUserInfo')) || {}, false];
    } catch (error) {
      return [{}, true];
    }
  })();
  const isAdmin = storedUser.role === 'Admin';
  const query = new URLSearchParams(location.search);
  const justConnected = query.get('sso_connected') === '1';
  const connectError = query.get('sso_error') === '1';
  const connectErrorReason = query.get('reason') || '';

  const activeProvider = SSO_PROVIDERS.find((provider) => provider.key === selectedProvider) || SSO_PROVIDERS[0];
  const providerLabel = t(activeProvider.labelKey);
  const SelectedSquareIcon = activeProvider.SquareIcon;
  const SelectedRoundIcon = activeProvider.RoundIcon;
  const directories = directoriesByProvider[selectedProvider] || [];
  const activeDirectories = directories.filter((directory) => directory.isActive);
  const jitEnabledAnywhere = activeDirectories.some((directory) => directory.allowJitProvisioning);
  const canConfirmDisconnect = confirmText.trim().toLowerCase() === DISCONNECT_CONFIRM_WORD;

  /**
   * Loads both providers' connected directories and the company seat usage in
   * parallel, so the rail can show each provider's status and the panel the
   * selected one. Marks a load failure only when both provider reads failed.
   * @returns {Promise<void>} resolves once state reflects the fetched data.
   */
  const loadData = useCallback(async () => {
    const [microsoftDirs, googleDirs, usage] = await Promise.all([
      listSsoDirectories('microsoft'),
      listSsoDirectories('google'),
      getSsoSeatUsage(),
    ]);
    setLoadFailed(microsoftDirs === null && googleDirs === null);
    setDirectoriesByProvider({ microsoft: microsoftDirs || [], google: googleDirs || [] });
    setSeatUsage(usage);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (storedUserUnreadable) {
      toasterMsg(t('SSO_SETTINGS.LOAD_FAILED'), 'danger', 6000);
    }
  }, [storedUserUnreadable, t]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [isAdmin, loadData]);

  useEffect(() => {
    const handlePageShow = () => {
      setConnecting(false);
      setDisconnecting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    if (justConnected) {
      toasterMsg(t('SSO_SETTINGS.CONNECTED_OK'), 'success', 6000);
    }
  }, [justConnected, t]);

  useEffect(() => {
    if (connectError) {
      toasterMsg(connectErrorMessage(), 'danger', 6000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectError, connectErrorReason, t]);

  /**
   * Starts the admin connect flow for the selected provider: navigates to the
   * provider sign-in when a URL is returned, otherwise surfaces a generic failure.
   * @returns {Promise<void>} resolves as the browser navigates, or after the toast.
   */
  const onConnect = async () => {
    setConnecting(true);
    const redirectUrl = await startSsoConnect(selectedProvider);
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    toasterMsg(t('SSO_SETTINGS.ACTION_FAILED'), 'danger', 6000);
    setConnecting(false);
  };

  /**
   * Flips just-in-time provisioning for one directory of the selected provider and
   * patches only that directory in local state, leaving the other provider's list
   * untouched.
   * @param {object} directory - the directory row whose JIT flag is toggled.
   * @returns {Promise<void>} resolves once state reflects the change.
   */
  const onToggleJit = async (directory) => {
    setTogglingId(directory.id);
    const updated = await setSsoDirectoryJit(selectedProvider, directory.id, !directory.allowJitProvisioning);
    if (updated) {
      setDirectoriesByProvider((current) => ({
        ...current,
        [selectedProvider]: (current[selectedProvider] || []).map((item) => (item.id === updated.id ? updated : item)),
      }));
    } else {
      toasterMsg(t('SSO_SETTINGS.ACTION_FAILED'), 'danger', 6000);
    }
    setTogglingId(null);
  };

  /**
   * Closes the disconnect confirmation modal and clears the typed confirmation.
   * @returns {void}
   */
  const closeDisconnect = () => {
    setDisconnectTarget(null);
    setConfirmText('');
  };

  /**
   * Clears local auth state and returns the admin to the login page after their
   * own SSO session was revoked by disconnecting the directory they signed in
   * through. The server token is already dead; logOut is best-effort cleanup.
   * @returns {Promise<void>} resolves as the browser navigates away.
   */
  const signOutAfterSelfRevoke = async () => {
    await logOut().catch(() => null);
    localStorageService.remove('loggedInUserInfo');
    window.location.assign(`${window.location.origin}/login?signed_out=1`);
  };

  /**
   * Confirms the disconnect once the admin has typed the confirmation word. Signs
   * the admin out first if this action revoked their own session; otherwise reloads
   * the directories or surfaces a generic failure.
   * @returns {Promise<void>} resolves after the outcome is handled.
   */
  const onConfirmDisconnect = async () => {
    if (!disconnectTarget || !canConfirmDisconnect) {
      return;
    }
    setDisconnecting(true);
    const result = await disconnectSsoDirectory(selectedProvider, disconnectTarget.id);
    if (result.selfSignedOut) {
      await signOutAfterSelfRevoke();
      return;
    }
    setDisconnecting(false);
    closeDisconnect();
    if (result.disconnected) {
      loadData();
    } else {
      toasterMsg(t('SSO_SETTINGS.ACTION_FAILED'), 'danger', 6000);
    }
  };

  /**
   * Maps a known connect-failure reason from the callback redirect to its specific
   * message, falling back to the generic connect-failed copy for anything else.
   * @returns {string} the localized error message for the toast.
   */
  const connectErrorMessage = () => {
    const knownReasons = ['CONNECT_NOT_ADMIN', 'CONNECT_TENANT_CONFLICT', 'PROVIDER_ABORTED', 'STATE_EXPIRED'];
    if (knownReasons.includes(connectErrorReason)) {
      return t(`SSO_SETTINGS.REASON_${connectErrorReason}`);
    }
    return t('SSO_SETTINGS.CONNECT_FAILED');
  };

  /**
   * Picks the seat-usage badge tone to match Figma's 4-state CustomerPlanIndicator
   * (node 11329-1308763): blue for unlimited plans, orange once the seat limit is
   * reached, amber when only one seat remains, green otherwise.
   * @returns {string} the chip tone modifier.
   */
  const getSeatChipTone = () => {
    if (seatUsage.unlimited) {
      return 'blue';
    }
    if (typeof seatUsage.limit !== 'number') {
      return 'green';
    }
    if (seatUsage.used >= seatUsage.limit) {
      return 'orange';
    }
    if (seatUsage.limit - seatUsage.used <= 1) {
      return 'amber';
    }
    return 'green';
  };

  /**
   * Renders the seat-usage chip (used/limit or unlimited) with the tone from
   * getSeatChipTone, or nothing when usage has not loaded.
   * @returns {(JSX.Element|null)} the chip, or null.
   */
  const renderSeatChip = () => {
    if (!seatUsage) {
      return null;
    }
    const label = seatUsage.unlimited
      ? t('SSO_SETTINGS.SEATS_UNLIMITED', { used: seatUsage.used })
      : t('SSO_SETTINGS.SEATS_USED', { used: seatUsage.used, limit: seatUsage.limit });
    const tone = getSeatChipTone();
    return (
      <span className={`sso-chip sso-chip--${tone}`}>
        <UserIcon size={20} className="sso-chip__icon" color={`var(--color-${tone})`} />
        <span className="sso-chip__label">{label}</span>
      </span>
    );
  };

  /**
   * Renders the left rail listing every configured provider. The green fill marks
   * the SELECTED provider (one at a time, per the Figma design); connection state
   * is carried by the trailing icon — green ActiveIcon when a directory is
   * connected, red DisconnectIcon when not — so Google and Microsoft behave
   * identically whichever is selected.
   * @returns {JSX.Element} the provider rail.
   */
  const renderProviderRail = () => (
    <aside className="sso-rail">
      <div className="sso-rail__head">{t('SSO_SETTINGS.CONNECTIONS')}</div>
      <ul className="sso-rail__list">
        {SSO_PROVIDERS.map((provider) => {
          const RailIcon = provider.SquareIcon;
          const isSelected = provider.key === selectedProvider;
          const isConnected = (directoriesByProvider[provider.key] || []).some((directory) => directory.isActive);
          return (
            <li
              key={provider.key}
              className={`sso-rail__item ${isSelected ? 'sso-rail__item--selected' : ''}`}
            >
              <button type="button" className="sso-rail__btn" onClick={() => setSelectedProvider(provider.key)} aria-pressed={isSelected}>
                <RailIcon size={20} />
                <span className="sso-rail__label">{t(provider.labelKey)}</span>
                {isConnected ? <ActiveIcon size={20} /> : <DisconnectIcon size={20} />}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );

  /**
   * Renders one connected-directory card for the selected provider: its tenant id,
   * active/disconnected status, and (when active) the JIT toggle and disconnect
   * action.
   * @param {object} directory - the directory mapping to render.
   * @returns {JSX.Element} the directory card.
   */
  const renderCard = (directory) => (
    <article key={directory.id} className={`sso-card ${directory.isActive ? '' : 'sso-card--off'}`}>
      <div className="sso-card__top">
        <SelectedRoundIcon size={40} className="sso-card__logo" />
        <span className="sso-card__meta">
          <span className="sso-card__label">{t('SSO_SETTINGS.DIRECTORY_ID')}</span>
          <span className="sso-card__id" title={directory.providerTenantId}>{directory.providerTenantId}</span>
        </span>
        <span className={`sso-card__status ${directory.isActive ? '' : 'sso-card__status--off'}`}>
          {directory.isActive ? t('SSO_SETTINGS.STATUS_ACTIVE') : t('SSO_SETTINGS.STATUS_DISCONNECTED')}
        </span>
      </div>

      {directory.allowedEmailDomains?.length > 0 && (
        <div className="sso-card__domains">
          <span className="sso-card__label">{t('SSO_SETTINGS.ALLOWED_DOMAINS')}</span>
          <span className="sso-card__domains-list">{directory.allowedEmailDomains.join(', ')}</span>
          <span className="sso-card__domains-hint">{t('SSO_SETTINGS.ALLOWED_DOMAINS_HINT')}</span>
        </div>
      )}

      {directory.isActive && (
        <div className="sso-card__actions">
          <label className="sso-toggle" htmlFor={`sso-jit-${directory.id}`}>
            <input
              id={`sso-jit-${directory.id}`}
              type="checkbox"
              checked={directory.allowJitProvisioning}
              disabled={togglingId === directory.id}
              onChange={() => onToggleJit(directory)}
            />
            <span className="sso-toggle__track"><span className="sso-toggle__thumb" /></span>
            <span className="sso-toggle__title">{t('SSO_SETTINGS.JIT_TITLE')}</span>
          </label>
          <button type="button" className="sso-card__disconnect" onClick={() => setDisconnectTarget(directory)}>
            {t('SSO_SETTINGS.DISCONNECT')}
          </button>
        </div>
      )}
    </article>
  );

  if (!isAdmin) {
    return (
      <>
        <div className="page-title-filter sticky d-fex">
          <div className="title-container">
            <h4 className="title">{t('SSO_SETTINGS.PAGE_TITLE')}</h4>
          </div>
        </div>
        <p className="sso-page__note">{t('SSO_SETTINGS.ADMIN_ONLY')}</p>
      </>
    );
  }

  return (
    <>
      <div className="page-title-filter sticky d-fex">
        <div className="title-container">
          <h4 className="title">{t('SSO_SETTINGS.PAGE_TITLE')}</h4>
        </div>
      </div>

      <div className="sso-layout">
        {renderProviderRail()}

        <section className="sso-panel">
          <header className="sso-panel__head">
            <span className="sso-panel__title">{t('SSO_SETTINGS.TITLE', { provider: providerLabel })}</span>
            <span className="sso-panel__desc">{t('SSO_SETTINGS.DESCRIPTION', { provider: providerLabel })}</span>
            {renderSeatChip()}
          </header>

          <div className="sso-panel__body">
            {loadFailed && <div className="sso-banner sso-banner--err" role="alert">{t('SSO_SETTINGS.LOAD_FAILED')}</div>}

            {loading && (
              <div className="sso-loading" role="status" aria-live="polite">
                <span className="sso-loading__spinner" aria-hidden="true" />
                <span className="sso-loading__text">{t('SSO_SETTINGS.LOADING')}</span>
              </div>
            )}

            {!loading && (
              <>
                {directories.length === 0 ? (
                  <div className="sso-empty">
                    <SelectedRoundIcon size={40} className="sso-empty__icon" />
                    <div className="sso-empty__copy">
                      <p className="sso-empty__title">{t('SSO_SETTINGS.EMPTY_TITLE')}</p>
                      <p className="sso-empty__text">{t('SSO_SETTINGS.EMPTY_TEXT', { provider: providerLabel })}</p>
                    </div>
                  </div>
                ) : (
                  <div className="sso-grid">{directories.map(renderCard)}</div>
                )}

                <button type="button" className="sso-add-btn" disabled={connecting} onClick={onConnect}>
                  <SelectedSquareIcon size={24} className="sso-add-btn__logo" />
                  <span className="sso-add-btn__label">
                    {directories.length === 0
                      ? t('SSO_SETTINGS.CONNECT', { provider: providerLabel })
                      : t('SSO_SETTINGS.ADD_DIRECTORY', { provider: providerLabel })}
                  </span>
                </button>

                <p className="sso-note">
                  {jitEnabledAnywhere
                    ? t('SSO_SETTINGS.NOTE_JIT_ON', { provider: providerLabel })
                    : t('SSO_SETTINGS.NOTE_JIT_OFF', { provider: providerLabel })}
                </p>
              </>
            )}
          </div>
        </section>
      </div>

      <Modal show={Boolean(disconnectTarget)} onHide={closeDisconnect} centered dialogClassName="sso-modal">
        <div className="sso-modal__head">
          <span className="sso-modal__title">{t('SSO_SETTINGS.DISCONNECT_TITLE')}</span>
          <button type="button" className="sso-modal__close" onClick={closeDisconnect} aria-label={t('SSO_SETTINGS.CANCEL')}>
            <CloseIcon size={16} />
          </button>
        </div>
        <div className="sso-modal__body">
          <p className="sso-modal__lead">{t('SSO_SETTINGS.DISCONNECT_BODY')}</p>
          <p className="sso-modal__text">{t('SSO_SETTINGS.DISCONNECT_WARNING')}</p>
          <div className="sso-modal__actions">
            <label className="sso-confirm-field" htmlFor="sso-disconnect-confirm">
              <span className="sso-confirm-field__label">
                {t('SSO_SETTINGS.TYPE_PREFIX')}
                <strong>{DISCONNECT_CONFIRM_WORD}</strong>
                {t('SSO_SETTINGS.TYPE_SUFFIX')}
              </span>
              <input
                id="sso-disconnect-confirm"
                type="text"
                className="sso-confirm-field__input"
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                disabled={disconnecting}
              />
            </label>
            <div className="sso-modal__buttons">
              <button type="button" className="sso-btn-ghost" onClick={closeDisconnect} disabled={disconnecting}>
                {t('SSO_SETTINGS.CANCEL')}
              </button>
              <button
                type="button"
                className="sso-btn-confirm"
                onClick={onConfirmDisconnect}
                disabled={disconnecting || !canConfirmDisconnect}
              >
                {t('SSO_SETTINGS.DISCONNECT_CONFIRM')}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default SsoSettings;
